import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Autocomplete,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  ShoppingCart as POIcon,
  Receipt as InvoiceIcon,
  Calculate as CalculateIcon,
  AddCircle as AddItemIcon,
} from '@mui/icons-material';
import { procurementService, Vendor as ServiceVendor } from '../../services/procurementService';
import { api } from '../../services/apiClient';
import { projectService, Project } from '../../services/projectService';
import { TypePill, StatusDot } from '../../components/common/pageKit';

// PO classification -- drives which line-item fields matter and how the
// backend posts the eventual invoice (see postingService.js: MATERIAL -> GR-IR
// / inventory, SERVICE|SUBCONTRACTING -> expense, ASSET|CAPEX -> Capital WIP).
type PoType =
  | 'MATERIAL'
  | 'SERVICE'
  | 'CAPEX'
  | 'SUBCONTRACTING'
  | 'JOB_WORK'
  | 'CONSUMABLE'
  | 'ASSET'
  | 'IMPORT';

type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

type LineItemType = 'MATERIAL' | 'SERVICE' | 'ASSET' | 'EXPENSE' | 'SUBCONTRACTING';

const PO_TYPES: PoType[] = ['MATERIAL', 'SERVICE', 'CAPEX', 'SUBCONTRACTING', 'JOB_WORK', 'CONSUMABLE', 'ASSET', 'IMPORT'];
const PRIORITIES: Priority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const LINE_ITEM_TYPES: LineItemType[] = ['MATERIAL', 'SERVICE', 'ASSET', 'EXPENSE', 'SUBCONTRACTING'];

const TYPE_TINT: Record<string, string> = {
  MATERIAL: '#0284c7',
  SERVICE: '#7c3aed',
  CAPEX: '#dc2626',
  SUBCONTRACTING: '#d97706',
  JOB_WORK: '#059669',
  CONSUMABLE: '#64748b',
  ASSET: '#0f766e',
  IMPORT: '#be185d',
  OTHER: '#475569',
};

const PRIORITY_COLOR: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

// Contextual copy explaining what each PO Type means for downstream
// receiving/accounting -- shown as an info banner so the type isn't a silent
// classification field.
const PO_TYPE_INFO: Record<PoType, string> = {
  MATERIAL: 'Goods will be received into inventory via GRN; accrues to GR-IR clearing until invoiced.',
  SERVICE: 'Recorded as a service receipt (no stock); posts to expense. TDS may apply.',
  CAPEX: 'Capitalises to Capital Work-in-Progress (1020) on invoice; asset created at capitalisation (roadmap).',
  ASSET: 'Capitalises to Capital Work-in-Progress (1020) on invoice; asset created at capitalisation (roadmap).',
  SUBCONTRACTING: 'Components issued to the vendor; processing charges post to expense (component-issue tracking is roadmap).',
  CONSUMABLE: 'Consumable stock issued for internal use; posts to expense rather than saleable inventory.',
  JOB_WORK: 'Job-work order sent to the vendor for processing; conversion charges post to expense.',
  IMPORT: 'Import purchase order; customs duty, freight and forex postings apply at goods receipt / invoice.',
};

// Default line item_type from the header PO Type -- users can still override
// per line via the line's own Select.
const defaultLineItemType = (poType: PoType): LineItemType => {
  switch (poType) {
    case 'SERVICE': return 'SERVICE';
    case 'CAPEX':
    case 'ASSET': return 'ASSET';
    case 'SUBCONTRACTING': return 'SUBCONTRACTING';
    case 'CONSUMABLE':
    case 'MATERIAL':
    default: return 'MATERIAL';
  }
};

interface PurchaseOrder {
  id: string;
  po_number: string;
  vendor_id: string;
  vendor_name: string;
  vendor_code: string;
  po_date: string;
  delivery_date?: string;
  expected_delivery_date?: string;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
  notes?: string;
  status: 'PENDING' | 'APPROVED' | 'RECEIVED' | 'CANCELLED' | string;
  approval_status?: string;
  po_type?: PoType | string;
  priority?: Priority | string;
  department?: string | null;
  cost_center_id?: string | number | null;
  cost_center_name?: string | null;
  project_id?: string | number | null;
  project_name?: string | null;
  payment_terms?: string | null;
  created_at: string;
  items?: PurchaseOrderItem[];
}

interface PurchaseOrderItem {
  id?: string;
  product_id: string;
  product_name?: string;
  item_type?: LineItemType | string;
  line_description?: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  gst_rate?: number;  // from API
  gstRate: number;    // for form
  cgst_amount?: number;
  sgst_amount?: number;
  igst_amount?: number;
  line_total: number;
  hsn_code?: string;
  line_hsn_code?: string;
  line_uom?: string;
  unit?: string;
}

interface CostCenter {
  id: number | string;
  code?: string;
  name: string;
  isActive?: boolean;
}

interface Vendor {
  id: string;
  vendor_code: string;
  company_name: string;
  contact_person: string;
  state: string;
  gstin?: string;
  tds_category: string;
  is_active?: boolean;
}

interface Product {
  id: string;
  product_name: string;
  hsn_code: string;
  unit: string;
  gst_rate: number;
  purchase_price?: number;
  is_active?: boolean;
}

const PurchaseOrderPage: React.FC = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // Cost centers / projects are optional master data -- a tenant without
  // Finance/Projects module access (or the endpoint being unavailable) must
  // not block raising a purchase order, so both fetches fail soft to [].
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    po_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    expected_delivery_date: '',
    notes: '',
    po_type: 'MATERIAL' as PoType,
    priority: 'MEDIUM' as Priority,
    department: '',
    payment_terms: '',
    cost_center_id: '',
    project_id: '',
    items: [] as PurchaseOrderItem[],
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [calculations, setCalculations] = useState({
    subtotal: 0,
    gstAmount: 0,
    totalAmount: 0,
  });

  // Fetch data
  useEffect(() => {
    fetchPurchaseOrders();
    fetchVendors();
    fetchProducts();
    fetchCostCenters();
    fetchProjects();
  }, []);

  // Filter orders based on search and filters
  useEffect(() => {
    let filtered = purchaseOrders;

    if (searchTerm) {
      filtered = filtered.filter(po =>
        po.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.vendor_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (vendorFilter) {
      filtered = filtered.filter(po => po.vendor_id === vendorFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(po => po.status === statusFilter);
    }

    setFilteredOrders(filtered);
  }, [purchaseOrders, searchTerm, vendorFilter, statusFilter]);

  // Recalculate totals when items change
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => {
      const lineAmount = Number(item.quantity) * Number(item.unit_price);
      const discountAmount = (lineAmount * Number(item.discount_percent)) / 100;
      return sum + (lineAmount - discountAmount);
    }, 0);

    const gstAmount = formData.items.reduce((sum, item) => {
      const lineAmount = Number(item.quantity) * Number(item.unit_price);
      const discountAmount = (lineAmount * Number(item.discount_percent)) / 100;
      const taxableAmount = lineAmount - discountAmount;
      // Use gst_rate (from API) OR gstRate (from form)
      const gstRate = Number(item.gst_rate) || Number(item.gstRate) || 0;
      return sum + (taxableAmount * gstRate) / 100;
    }, 0);

    const totalAmount = subtotal + gstAmount;

    setCalculations({
      subtotal: Math.round(subtotal * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
    });
  }, [formData.items]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ purchaseOrders: PurchaseOrder[] }>('/procurement/purchase-orders');
      setPurchaseOrders(response.purchaseOrders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await procurementService.getVendors();
      setVendors(response.vendors.filter((vendor: ServiceVendor) => vendor.is_active));
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get<{ products: Product[] }>('/products');
      setProducts(response.products.filter((product: Product) => product.is_active));
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchCostCenters = async () => {
    try {
      const response = await api.get<{ costCenters: CostCenter[] }>('/cost-centers');
      setCostCenters((response.costCenters || []).filter((c) => c.isActive !== false));
    } catch (err) {
      // Optional master data -- module may not be enabled for this tenant.
      setCostCenters([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const list = await projectService.getProjects();
      setProjects(list || []);
    } catch (err) {
      // Optional master data -- module may not be enabled for this tenant.
      setProjects([]);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.vendor_id) {
      errors.vendor_id = 'Vendor is required';
    }

    if (!formData.po_date) {
      errors.po_date = 'PO date is required';
    }

    if (formData.items.length === 0) {
      errors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      const lineType = (item.item_type as LineItemType) || 'MATERIAL';
      if (lineType === 'MATERIAL') {
        if (!item.product_id) {
          errors[`item_${index}_product`] = 'Item is required';
        }
      } else if (!item.line_description || !item.line_description.trim()) {
        errors[`item_${index}_description`] = 'Description is required';
      }
      if (item.quantity <= 0) {
        errors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unit_price <= 0) {
        errors[`item_${index}_price`] = 'Unit price must be greater than 0';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Transform snake_case to camelCase for backend
      const payload = {
        vendorId: formData.vendor_id,
        poDate: formData.po_date,
        deliveryDate: formData.delivery_date || null,
        expectedDeliveryDate: formData.expected_delivery_date || null,
        notes: formData.notes,
        poType: formData.po_type,
        priority: formData.priority,
        department: formData.department || null,
        paymentTerms: formData.payment_terms || null,
        costCenterId: formData.cost_center_id || null,
        projectId: formData.project_id || null,
        items: formData.items.map(item => ({
          productId: item.product_id,
          itemType: item.item_type || defaultLineItemType(formData.po_type),
          lineDescription: item.line_description || '',
          hsnCode: item.hsn_code || '',
          uom: item.unit || '',
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercent: item.discount_percent,
          gstRate: Number(item.gstRate) || 18
        }))
      };

      if (dialogMode === 'create') {
        const response = await api.post<{ purchaseOrder: PurchaseOrder }>('/procurement/purchase-orders', payload);
        setSnackbar({ open: true, message: 'Purchase order created successfully', severity: 'success' });
        setOpenDialog(false);
        resetForm();
        const newId = response?.purchaseOrder?.id;
        if (newId) {
          // New POs are created as DRAFT -- submission/approval happens on
          // the PO Detail page, so route there instead of just closing.
          navigate(`/procurement/orders/${newId}`);
          return;
        }
        await fetchPurchaseOrders();
      } else {
        await api.put(`/procurement/purchase-orders/${selectedPO?.id}`, payload);
        setSnackbar({ open: true, message: 'Purchase order updated successfully', severity: 'success' });
        setOpenDialog(false);
        resetForm();
        await fetchPurchaseOrders();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save purchase order';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      po_date: new Date().toISOString().split('T')[0],
      delivery_date: '',
      expected_delivery_date: '',
      notes: '',
      po_type: 'MATERIAL',
      priority: 'MEDIUM',
      department: '',
      payment_terms: '',
      cost_center_id: '',
      project_id: '',
      items: [],
    });
    setFormErrors({});
    setSelectedPO(null);
  };

  const handleCreate = () => {
    setDialogMode('create');
    resetForm();
    setOpenDialog(true);
  };

  const handleView = async (po: PurchaseOrder) => {
    try {
      const response = await api.get<{ purchaseOrder: PurchaseOrder }>(`/procurement/purchase-orders/${po.id}`);
      const fullPO = response.purchaseOrder;

      setDialogMode('view');
      setSelectedPO(fullPO);
      setFormData({
        vendor_id: fullPO.vendor_id,
        po_date: fullPO.po_date.split('T')[0],
        delivery_date: fullPO.delivery_date ? fullPO.delivery_date.split('T')[0] : '',
        expected_delivery_date: fullPO.expected_delivery_date ? fullPO.expected_delivery_date.split('T')[0] : '',
        notes: fullPO.notes || '',
        po_type: ((fullPO.po_type as PoType) || 'MATERIAL'),
        priority: ((fullPO.priority as Priority) || 'MEDIUM'),
        department: fullPO.department || '',
        payment_terms: fullPO.payment_terms || '',
        cost_center_id: fullPO.cost_center_id ? String(fullPO.cost_center_id) : '',
        project_id: fullPO.project_id ? String(fullPO.project_id) : '',
        items: (fullPO.items || []).map(item => ({
          ...item,
          item_type: item.item_type || 'MATERIAL',
          line_description: item.line_description || '',
          hsn_code: item.line_hsn_code || item.hsn_code || '',
          unit: item.line_uom || item.unit || '',
          gstRate: Number(item.gst_rate ?? item.gstRate ?? 18),
        })),
      });
      setOpenDialog(true);
    } catch (err) {
      setError('Failed to load purchase order details');
    }
  };

  const addItem = () => {
    setFormData(prev => {
      const newItem: PurchaseOrderItem = {
        product_id: '',
        item_type: defaultLineItemType(prev.po_type),
        line_description: '',
        hsn_code: '',
        unit: '',
        quantity: 1,
        unit_price: 0,
        discount_percent: 0,
        gstRate: 18,
        line_total: 0,
      };
      return { ...prev, items: [...prev.items, newItem] };
    });
  };

  const updateItem = (index: number, field: keyof PurchaseOrderItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'info';
      case 'RECEIVED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Purchase Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Purchase Order
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <POIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {purchaseOrders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total POs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <POIcon color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {purchaseOrders.filter(po => po.status === 'PENDING').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InvoiceIcon color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {purchaseOrders.filter(po => po.status === 'RECEIVED').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Received
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CalculateIcon color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    ₹{(purchaseOrders.reduce((sum, po) => sum + po.total_amount, 0) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Value
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Search Purchase Orders"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  label="Vendor"
                >
                  <MenuItem value="">All Vendors</MenuItem>
                  {vendors.map(vendor => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="RECEIVED">Received</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredOrders.length} of {purchaseOrders.length} orders
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>PO Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>PO Date</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Received</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Exception</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((po) => (
              <TableRow key={po.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {po.po_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    <TypePill
                      label={(po.po_type || 'MATERIAL').toString().replace(/_/g, ' ')}
                      tint={TYPE_TINT[po.po_type || 'MATERIAL'] || TYPE_TINT.OTHER}
                    />
                    {po.priority && (
                      <Chip
                        label={po.priority}
                        color={PRIORITY_COLOR[po.priority] || 'default'}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {po.vendor_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {po.vendor_code}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(po.po_date).toLocaleDateString('en-IN')}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    ₹{po.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={po.status}
                    color={getStatusColor(po.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {po.status === 'FULLY_RECEIVED' || po.status === 'RECEIVED' || po.status === 'DELIVERED' ? (
                    <StatusDot label="Full" tone="success" />
                  ) : po.status === 'PARTIALLY_RECEIVED' ? (
                    <StatusDot label="Partial" tone="warning" />
                  ) : po.status === 'CANCELLED' ? (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  ) : (
                    <StatusDot label="Not received" tone="neutral" />
                  )}
                </TableCell>
                <TableCell>
                  {po.approval_status ? (
                    <Chip
                      label={po.approval_status.replace(/_/g, ' ')}
                      color={po.approval_status === 'APPROVED' ? 'success' : po.approval_status === 'REJECTED' ? 'error' : po.approval_status === 'PENDING_APPROVAL' ? 'warning' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {(po.approval_status === 'PENDING_APPROVAL' || po.status === 'REJECTED') ? (
                    <StatusDot
                      label={po.status === 'REJECTED' ? 'Rejected' : 'Awaiting Approval'}
                      tone="error"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleView(po)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No purchase orders found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Purchase Order Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' && 'Create Purchase Order'}
          {dialogMode === 'edit' && 'Edit Purchase Order'}
          {dialogMode === 'view' && `Purchase Order - ${selectedPO?.po_number}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* PO Type drives downstream receiving/accounting -- shown first
                so it's the first decision the user makes on the form. */}
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>PO Type</InputLabel>
                <Select
                  value={formData.po_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, po_type: e.target.value as PoType }))}
                  label="PO Type"
                  disabled={dialogMode === 'view'}
                >
                  {PO_TYPES.map(t => (
                    <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as Priority }))}
                  label="Priority"
                  disabled={dialogMode === 'view'}
                >
                  {PRIORITIES.map(p => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                value={vendors.find(v => v.id === formData.vendor_id) || null}
                onChange={(event, newValue) => setFormData(prev => ({ ...prev, vendor_id: newValue?.id || '' }))}
                options={vendors}
                getOptionLabel={(option) => `${option.company_name} (${option.vendor_code})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={dialogMode === 'view'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor *"
                    error={!!formErrors.vendor_id}
                    helperText={formErrors.vendor_id}
                    required
                  />
                )}
              />
            </Grid>

            {/* Contextual banner -- changes with PO Type so the type isn't a
                silent classification field; it explains what happens next. */}
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                {PO_TYPE_INFO[formData.po_type] || PO_TYPE_INFO.MATERIAL}
              </Alert>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="PO Date"
                type="date"
                value={formData.po_date}
                onChange={(e) => setFormData(prev => ({ ...prev, po_date: e.target.value }))}
                error={!!formErrors.po_date}
                helperText={formErrors.po_date}
                disabled={dialogMode === 'view'}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Delivery Date"
                type="date"
                value={formData.delivery_date}
                onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                disabled={dialogMode === 'view'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Expected Delivery Date"
                type="date"
                value={formData.expected_delivery_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                disabled={dialogMode === 'view'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Payment Terms"
                placeholder="e.g. Net 30"
                value={formData.payment_terms}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            {(costCenters.length > 0 || (dialogMode === 'view' && selectedPO?.cost_center_name)) && (
              <Grid size={{ xs: 12, md: 3 }}>
                {dialogMode === 'view' ? (
                  <TextField
                    fullWidth
                    label="Cost Center"
                    value={selectedPO?.cost_center_name || 'None'}
                    disabled
                  />
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Cost Center</InputLabel>
                    <Select
                      value={formData.cost_center_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost_center_id: e.target.value }))}
                      label="Cost Center"
                    >
                      <MenuItem value="">None</MenuItem>
                      {costCenters.map(cc => (
                        <MenuItem key={cc.id} value={cc.id}>
                          {cc.code ? `${cc.code} - ${cc.name}` : cc.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
            )}
            {(projects.length > 0 || (dialogMode === 'view' && selectedPO?.project_name)) && (
              <Grid size={{ xs: 12, md: 3 }}>
                {dialogMode === 'view' ? (
                  <TextField
                    fullWidth
                    label="Project"
                    value={selectedPO?.project_name || 'None'}
                    disabled
                  />
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={formData.project_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, project_id: e.target.value }))}
                      label="Project"
                    >
                      <MenuItem value="">None</MenuItem>
                      {projects.map(p => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.project_code} - {p.project_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Purchase Order Items</Typography>
                {dialogMode !== 'view' && (
                  <Button
                    variant="outlined"
                    startIcon={<AddItemIcon />}
                    onClick={addItem}
                  >
                    Add Item
                  </Button>
                )}
              </Box>
              
              {formData.items.map((item, index) => {
                const lineType = (item.item_type as LineItemType) || 'MATERIAL';
                const isMaterial = lineType === 'MATERIAL';
                const codeLabel = lineType === 'SERVICE' || lineType === 'SUBCONTRACTING' ? 'SAC' : 'HSN';
                return (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 1.5 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Item Type</InputLabel>
                          <Select
                            value={lineType}
                            onChange={(e) => updateItem(index, 'item_type', e.target.value)}
                            label="Item Type"
                            disabled={dialogMode === 'view'}
                          >
                            {LINE_ITEM_TYPES.map(t => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: isMaterial ? 3 : 2 }}>
                        <Autocomplete
                          value={products.find(p => p.id === item.product_id) || null}
                          onChange={(event, newValue) => {
                            updateItem(index, 'product_id', newValue?.id || '');
                            updateItem(index, 'gstRate', newValue?.gst_rate || 18);
                            updateItem(index, 'unit_price', newValue?.purchase_price || 0);
                            if (isMaterial) {
                              updateItem(index, 'hsn_code', newValue?.hsn_code || '');
                              updateItem(index, 'unit', newValue?.unit || '');
                            }
                          }}
                          options={products}
                          getOptionLabel={(option) => `${option.product_name} (${option.hsn_code})`}
                          disabled={dialogMode === 'view'}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Item / Service"
                              error={!!formErrors[`item_${index}_product`]}
                              helperText={formErrors[`item_${index}_product`] || (!isMaterial ? 'Optional for non-material lines' : undefined)}
                              required={isMaterial}
                            />
                          )}
                        />
                      </Grid>
                      {!isMaterial && (
                        <Grid size={{ xs: 12, md: 2.5 }}>
                          <TextField
                            fullWidth
                            label="Description"
                            placeholder={lineType === 'ASSET' ? 'Asset detail' : 'Line description'}
                            value={item.line_description || ''}
                            onChange={(e) => updateItem(index, 'line_description', e.target.value)}
                            error={!!formErrors[`item_${index}_description`]}
                            helperText={formErrors[`item_${index}_description`]}
                            disabled={dialogMode === 'view'}
                            required
                          />
                        </Grid>
                      )}
                      <Grid size={{ xs: 6, md: 1 }}>
                        <TextField
                          fullWidth
                          label={codeLabel}
                          value={item.hsn_code || ''}
                          onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                          disabled={dialogMode === 'view'}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 1 }}>
                        <TextField
                          fullWidth
                          label="UOM"
                          value={item.unit || ''}
                          onChange={(e) => updateItem(index, 'unit', e.target.value)}
                          disabled={dialogMode === 'view'}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 1 }}>
                        <TextField
                          fullWidth
                          label={lineType === 'SERVICE' ? 'Qty/Hrs' : 'Qty'}
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          error={!!formErrors[`item_${index}_quantity`]}
                          helperText={formErrors[`item_${index}_quantity`]}
                          disabled={dialogMode === 'view'}
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 1.2 }}>
                        <TextField
                          fullWidth
                          label="Rate"
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          error={!!formErrors[`item_${index}_price`]}
                          helperText={formErrors[`item_${index}_price`]}
                          disabled={dialogMode === 'view'}
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 1 }}>
                        <TextField
                          fullWidth
                          label="Disc %"
                          type="number"
                          value={item.discount_percent}
                          onChange={(e) => updateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                          disabled={dialogMode === 'view'}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 1 }}>
                        <TextField
                          fullWidth
                          label="GST %"
                          type="number"
                          value={item.gst_rate ?? item.gstRate}
                          onChange={(e) => updateItem(index, 'gstRate', parseFloat(e.target.value) || 0)}
                          disabled={dialogMode === 'view'}
                        />
                      </Grid>
                      <Grid size={{ xs: 8, md: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Line Total: ₹{(
                            (Number(item.quantity) * Number(item.unit_price) * (1 - Number(item.discount_percent) / 100)) *
                            (1 + (Number(item.gst_rate) || Number(item.gstRate) || 0) / 100)
                          ).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4, md: 0.8 }}>
                        {dialogMode !== 'view' && (
                          <IconButton
                            color="error"
                            onClick={() => removeItem(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                );
              })}
              
              {formErrors.items && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {formErrors.items}
                </Alert>
              )}
            </Grid>
            
            {/* Totals */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body1">
                        Subtotal: ₹{calculations.subtotal.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body1">
                        GST Amount: ₹{calculations.gstAmount.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" color="primary">
                        Total Amount: ₹{calculations.totalAmount.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode !== 'view' && (
            <Button
              variant="contained"
              onClick={handleSubmit}
            >
              {dialogMode === 'create' ? 'Create Purchase Order' : 'Update Purchase Order'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseOrderPage;